import React, {MutableRefObject, useEffect, useState} from 'react';
import {
    Card,
    Block,
    Dialog,
    DialogBody,
    DialogFooter,
    Button,
    Col,
    Row,
    Icon,
    Space,
    Menu,
    MenuItem,
    NoData,
    GroupContainer,
    MenuDivider,
    Text,
    Modal,
    ModalComponentProps,
} from '@mc/ui-v4';

import {mcNew, mcSettings} from '@mc/react-ui-icons';

import {EnumTopicType, LayerSettingsApi, LayerVO, RightsApi, TopicTreeNodeVO, TopicVO, UserRightsVO} from '../../../api';
import {UserLayerConfiguration} from './UserLayerConfiguration';
import {TopicConfiguration} from './TopicConfiguration';
import TopicTreeMenu, {restData} from '../../tree/TopicTreeMenu';
import {CACHE_KEY_LAYER_TOPIC, CACHE_KEY_TOPICS_OWNED, CACHE_KEY_USER_LAYER_OPRAVNENI} from '../../../queryKeys';
import {WaitForQueryData} from '@mc/react-app-v4';
import {useQuery, UseQueryResult} from 'react-query';
import {CatalogConfiguration} from './CatalogConfiguration';
import {NodeModel} from '@minoru/react-dnd-treeview';
import {NewEditTopic} from './NewEditTopic';
import {useTopicsMap} from '../../../hooks/useTopicsMap';
import {useLayersConfig} from '../../../hooks/useLayersConfig';
import {useMapioContext} from '../../../mapioContext';
import {LayerId, MapEditorActions} from '@mc/react-mapio';
import './ConfigurationModal.scss';
import {isUserDbLayer} from '../../../helpers/LayerConfigUtils';
import {useAuth} from '@mc/react-auth';
import {JsonImportLayerProgress} from '../JsonImportModal/JsonImportLayerProgress';

type Props = ModalComponentProps & {
    onLayerDelete: (layerId: LayerId) => void;
    mapRef: MutableRefObject<MapEditorActions>;
};

export const ConfigurationModal: React.FC<Props> = ({onClose, onLayerDelete, mapRef}) => {
    const topicsMapQuery: UseQueryResult<TopicVO[]> = useTopicsMap();
    const mapioContext = useMapioContext();
    const {topicId} = mapioContext.value;
    const {setTopicId} = mapioContext;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();

    const [selectedMenuItem, setSelectedMenuItem] = useState<NodeModel<restData>>();
    const [selectedMenuItemId, setSelectedMenuItemId] = useState<number | null>(null);
    const [layerIds, setLayerIds] = useState<number[]>([]);

    const userOpravneniQuery = useQuery<UserRightsVO[]>(
        [CACHE_KEY_USER_LAYER_OPRAVNENI, layerIds],
        async () => (await RightsApi.getAllLayerRightsForLoggedInUser(layerIds)).data,
        {enabled: layerIds.length > 0},
    );
    const useTopicQuery = useQuery<TopicTreeNodeVO[]>([CACHE_KEY_LAYER_TOPIC, topicId], async () => (await LayerSettingsApi.getTopicTree(topicId)).data, {
        cacheTime: 0,
        enabled: topicId && topicsMapQuery.data?.some(item => item.id === topicId),
    });
    const useTopicsOwnedQuery = useQuery<string[]>([CACHE_KEY_TOPICS_OWNED], async () => (await RightsApi.getTopicsOwned()).data);
    const {data: auth} = useAuth();

    const mamPravoNaEditaciTopicu = (topic: TopicVO) => {
        if (!topic) {
            return false;
        }
        return useTopicsOwnedQuery.data?.includes(topic.id) || auth?.admin || topic.typ === EnumTopicType.APP_TOPIC;
    };

    // Během refetche konfigurace témat (hned po založení nového tématu) vidí komponenta ještě starý
    // seznam témat - přepnutí na první dostupné téma by zrušilo přepnutí na právě založené téma (MCW-29126).
    const jeKonfiguraceTematAktualni = topicsMapQuery.isFetched && !topicsMapQuery.isFetching;

    useEffect(() => {
        if (!jeKonfiguraceTematAktualni || !topicsMapQuery.data?.length) {
            return;
        }
        if (!topicId || !topicsMapQuery.data.some(item => item.id === topicId)) {
            setTopicId(topicsMapQuery.data[0].id);
        }
    }, [topicsMapQuery.data, jeKonfiguraceTematAktualni]);

    useEffect(() => {
        if (!jeKonfiguraceTematAktualni) {
            return;
        }
        if (topicId && !mamPravoNaEditaciTopicu(topicsMapQuery.data.find(item => item.id === topicId)!)) {
            const prvniTopicNaKterejMamPravo = topicsMapQuery.data.find(item => mamPravoNaEditaciTopicu(item));
            if (prvniTopicNaKterejMamPravo) {
                setTopicId(prvniTopicNaKterejMamPravo.id);
            } else {
                setTopicId(undefined);
                console.error('Uživatel nemá právo na žádný topic');
            }
        }
    }, [useTopicsOwnedQuery.data, topicsMapQuery.data, jeKonfiguraceTematAktualni]);

    useEffect(() => {
        //nastavení vybraného prvku
        if (useTopicQuery.data) {
            const layerIds = useTopicQuery.data
                .filter(item => item.layerId)
                .map(item => item.layerId)
                .filter((value, index, self) => self.indexOf(value) === index);
            setLayerIds(layerIds);
            if (selectedMenuItem) {
                const newSelected = useTopicQuery.data.find(item => selectedMenuItem.data?.catalogItemId === item.catalogItemId);
                setSelectedMenuItemId(newSelected?.catalogItemId ?? null);
            }
        }
    }, [useTopicQuery.data]);

    useEffect(() => {
        if (useTopicQuery.data) {
            const layerIds = useTopicQuery.data
                .filter(item => item.layerId)
                .map(item => item.layerId)
                .filter((value, index, self) => self.indexOf(value) === index);
            setLayerIds(layerIds);
            const newSelected = useTopicQuery.data.find(item => selectedMenuItemId === item.catalogItemId);
            setSelectedMenuItem(
                newSelected
                    ? {
                          id: newSelected.id,
                          parent: newSelected.parentId ?? 0,
                          text: newSelected.text ?? '-',
                          droppable: !newSelected.layerId,
                          data: {
                              catalogItemId: newSelected.catalogItemId,
                              selected: newSelected.selected,
                              layerId: newSelected.layerId,
                              topicId: newSelected.topicId,
                          },
                      }
                    : undefined,
            );
        }
    }, [selectedMenuItemId, useTopicQuery.data]);

    const createTopic = async () => {
        Modal.show(({onClose}) => <NewEditTopic mapRef={mapRef} setTopicId={setTopicId} onClose={onClose} />);
    };

    return (
        <Dialog
            className={'configuration-modal'}
            showOnTop
            size="xxl"
            onClose={onClose}
            // @ts-ignore
            title={
                <Space className={'align-items-center'}>
                    <Icon icon={mcSettings} />
                    {topicsMapQuery.data?.find(item => item.id === topicId)?.label || 'Téma nevybráno'}
                    <Button
                        style={'link'}
                        menu={
                            <Menu>
                                <WaitForQueryData queries={[useTopicsOwnedQuery]}>
                                    {() => {
                                        const temata = topicsMapQuery.data?.filter((item: TopicVO) => {
                                            return mamPravoNaEditaciTopicu(item);
                                        });
                                        return (
                                            temata &&
                                            temata.length > 0 && (
                                                <>
                                                    <Text className="pl-3" intent={'inactive'}>
                                                        Přepnout na téma
                                                    </Text>
                                                    {temata.map(item => (
                                                        <MenuItem
                                                            active={item.id === topicId}
                                                            onClick={() => setTopicId(item.id)}
                                                            text={item.label}
                                                            key={item.id}
                                                        />
                                                    ))}
                                                    <MenuDivider />
                                                </>
                                            )
                                        );
                                    }}
                                </WaitForQueryData>

                                <MenuItem
                                    onClick={createTopic}
                                    text={
                                        <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                            Nové téma
                                        </Text>
                                    }
                                />
                            </Menu>
                        }
                        popupProps={{position: 'bottom center'}}
                        dropdown
                    />
                </Space>
            }
        >
            <DialogBody disableScroll>
                <WaitForQueryData queries={[topicsMapQuery]}>
                    {() => {
                        return (
                            <Row className={'w-100'}>
                                <Col xs={4} className={'h-100'}>
                                    <Card mode={'edit'} className={'h-100'} bodyClassName={'h-100'}>
                                        {topicId ? (
                                            <WaitForQueryData queries={[useTopicQuery, userOpravneniQuery]}>
                                                {() => {
                                                    const opravneniData = userOpravneniQuery.data;
                                                    const layersIsVlastnik = opravneniData?.[0]?.layers?.map(l => l.vlastnikVrstvy && l.layer?.id) ?? [];
                                                    return (
                                                        <Block className={'h-100'} bodyClassName={'h-100 overflow-y-auto p-0'}>
                                                            <TopicTreeMenu
                                                                topicId={topicId}
                                                                mapRef={mapRef}
                                                                setTopicOnDelete={() => setTopicId(topicsMapQuery.data[0]?.id)}
                                                                layersIsVlastnik={layersIsVlastnik}
                                                                data={useTopicQuery.data || []}
                                                                selectedMenuItemId={selectedMenuItemId}
                                                                setSelectedMenuItemId={setSelectedMenuItemId}
                                                                onLayerDelete={onLayerDelete}
                                                            />
                                                        </Block>
                                                    );
                                                }}
                                            </WaitForQueryData>
                                        ) : (
                                            <NoData>Vyberte téma</NoData>
                                        )}
                                    </Card>
                                </Col>
                                <Col xs={8} className={'h-100 config-right-pane'}>
                                    <WaitForQueryData queries={[layersConfigQuery]}>
                                        {() => {
                                            const layers = layersConfigQuery.data;

                                            return (
                                                <>
                                                    {selectedMenuItem?.data?.layerId ? (
                                                        <JsonImportLayerProgress layerId={selectedMenuItem.data.layerId} />
                                                    ) : null}
                                                    {selectedMenuItem ? (
                                                        <>
                                                            {selectedMenuItem.data?.layerId ? (
                                                                <>
                                                                    {isUserDbLayer(layers, selectedMenuItem.data?.layerId) ? (
                                                                        <UserLayerConfiguration
                                                                            layer={layers[selectedMenuItem?.data.layerId]}
                                                                            topicId={topicId}
                                                                            mapRef={mapRef}
                                                                        />
                                                                    ) : (
                                                                        <GroupContainer title={selectedMenuItem.text}>
                                                                            <Card mode={'view'}>
                                                                                <NoData>Konfigurace daného typu není podporována</NoData>
                                                                            </Card>
                                                                        </GroupContainer>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {selectedMenuItem.parent === 0 ? (
                                                                        <TopicConfiguration topicId={selectedMenuItem.data?.topicId} mapRef={mapRef} />
                                                                    ) : (
                                                                        <CatalogConfiguration
                                                                            catalog={selectedMenuItem}
                                                                            catalogLayers={layers}
                                                                            topicId={topicId}
                                                                        />
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <NoData>Vyberte položku ze stromu</NoData>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </WaitForQueryData>
                                </Col>
                            </Row>
                        );
                    }}
                </WaitForQueryData>
            </DialogBody>
            <DialogFooter>
                <Button
                    style="primary"
                    onClick={() => {
                        mapRef.current.updateTopic(topicId);
                        onClose();
                    }}
                >
                    Zavřít
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
